import React, { useMemo } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { sanitizeCoordinate } from "../../utils/helper";
import { getMarkerIcon, isValidCoordinate } from "../../utils/helper";
import { MAP_ICON_SIZE, DETAILS_MAP_ZOOM_LEVEL } from "../../constants/dimensions";

const MAP_CONTAINER_STYLE = { height: "100%", width: "100%" };

const MAP_OPTIONS = {
  clickableIcons: false,
  fullscreenControl: false,
  cameraControl: false,
  mapTypeControl: false,
  gestureHandling: "none",
};

const ERROR_CONTAINER_STYLE = {
  height: "100%",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#f5f5f5",
  color: "#666",
  padding: "20px",
  textAlign: "center",
};

/**
 * Renders a static Google Map with a marker at the specified coordinates.
 * This component displays a single location and is typically used in detail views.
 *
 * @param {Object} props - The component props.
 * @param {string} props.name - The name to be displayed as the marker's title.
 * @param {number} props.lat - The latitude coordinate for the marker.
 * @param {number} props.lng - The longitude coordinate for the marker.
 * @returns {JSX.Element} The rendered Google Map with a marker.
 *
 * @example
 * <DetailsMapView name="City Hall" lat={36.8508} lng={-76.2859} />
 */
const DetailsMapView = ({ name = "Park", lat, lng }) => {
  // Memoize sanitized coordinates
  const sanitizedPosition = useMemo(() => {
    const sanitizedLat = sanitizeCoordinate(lat);
    const sanitizedLng = sanitizeCoordinate(lng);

    if (!isValidCoordinate(sanitizedLat, sanitizedLng)) {
      console.error("DetailsMapView: Invalid coordinates", {
        name,
        input: { lat, lng },
        sanitized: { sanitizedLat, sanitizedLng },
      });
      return null;
    }

    return {
      lat: sanitizedLat,
      lng: sanitizedLng,
    };

  }, [lat, lng, name]);

  // Memoize marker icon
  const markerIcon = useMemo(
    () => ({
      url: getMarkerIcon("default", "enabled"),
      scaledSize: MAP_ICON_SIZE,
    }),
    []
  );

  // Handle invalid coordinates
  if (!sanitizedPosition) {
    return (
      <div style={ERROR_CONTAINER_STYLE} role="alert">
        <p style={{ fontSize: "16px", fontWeight: "bold", margin: 0 }}>
          Unable to display map
        </p>
        <p style={{ fontSize: "14px", marginTop: "8px" }}>
          Invalid location coordinates
        </p>
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={MAP_CONTAINER_STYLE}
      center={sanitizedPosition}
      zoom={DETAILS_MAP_ZOOM_LEVEL}
      options={MAP_OPTIONS}
    >
      <Marker
        position={sanitizedPosition}
        icon={markerIcon}
        title={name}
        aria-label={`Location marker for ${name}`}
      />
    </GoogleMap>
  );
};

export default DetailsMapView;
